import { useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';
import { t } from '../lib/i18n';

/** Returns a copy of `items` with the item at `from` moved to `to` (both clamped to the list). */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || from >= next.length) return next;
  const target = Math.max(0, Math.min(next.length - 1, to));
  const [item] = next.splice(from, 1);
  next.splice(target, 0, item as T);
  return next;
}

/**
 * A re-orderable list. Mouse and touch users drag the grip; everyone (and every keyboard or screen-reader
 * user) can use the move up / move down buttons, so dragging is never the only way.
 */
export function SortableList<T>({
  items,
  getKey,
  getLabel,
  renderItem,
  onChange,
  label,
  disabled = false,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  /** Plain-text name of an item, used in button labels and announcements. */
  getLabel: (item: T) => string;
  renderItem: (item: T, index: number) => ReactNode;
  onChange: (next: T[]) => void;
  /** Accessible name of the list. */
  label: string;
  disabled?: boolean;
}) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const move = (from: number, to: number) => {
    if (to === from || to < 0 || to >= items.length) return;
    const item = items[from];
    onChange(moveItem(items, from, to));
    if (item !== undefined) {
      setAnnouncement(t('widget.sort.moved', { name: getLabel(item), position: to + 1, total: items.length }));
    }
  };

  return (
    <div>
      <ul aria-label={label} className="space-y-2">
        {items.map((item, index) => {
          const name = getLabel(item);
          const dragging = dragFrom === index;
          return (
            <li
              key={getKey(item)}
              draggable={!disabled}
              onDragStart={(e) => {
                setDragFrom(index);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(index));
              }}
              onDragOver={(e) => {
                if (dragFrom === null) return;
                e.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragFrom !== null) move(dragFrom, index);
                setDragFrom(null);
                setOverIndex(null);
              }}
              onDragEnd={() => {
                setDragFrom(null);
                setOverIndex(null);
              }}
              className={`flex items-center gap-2 rounded-lg border bg-white p-2 ${
                overIndex === index && dragFrom !== null && dragFrom !== index ? 'border-brand' : 'border-slate-200'
              } ${dragging ? 'opacity-50' : ''}`}
            >
              <span
                aria-hidden="true"
                title={t('widget.sort.handle', { name })}
                className={`inline-flex h-8 w-6 shrink-0 items-center justify-center text-slate-500 ${disabled ? '' : 'cursor-grab'}`}
              >
                <GripVertical className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">{renderItem(item, index)}</div>
              {disabled ? null : (
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    aria-label={t('widget.sort.moveUp', { name })}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    <ArrowUp aria-hidden="true" className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={t('widget.sort.moveDown', { name })}
                    disabled={index === items.length - 1}
                    onClick={() => move(index, index + 1)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    <ArrowDown aria-hidden="true" className="h-4 w-4" />
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
